import { FileEdit, Search, Globe, List, CheckCircle2 } from 'lucide-react';
import { LucideIcon } from 'lucide-react';

export type ToolCategory = 'edit' | 'search' | 'web' | 'list' | 'default';

export interface ToolConfig {
  category: ToolCategory;
  displayName?: string; // Alias para mostrar (opcional, si no se proporciona usa el nombre original)
  icon?: LucideIcon; // Icono específico para esta tool
  assetPath?: string; // Ruta al SVG personalizado
  iconColor?: string; // Color del icono
  hidden?: boolean; // Si true, la tool no se muestra en UI
}

// Mapa de tools a configuración
// La clave es el nombre normalizado de la tool (lowercase, sin guiones)
const toolConfigs: Record<string, ToolConfig> = {
  // Tools de edición de documentos Word
  'wordsearchreplace': { 
    category: 'edit',
    displayName: 'Search and replace'
  },
  'wordfindtextindocument': { 
    category: 'edit',
    displayName: 'Find text'
  },
  'wordreplacemultiple': { 
    category: 'edit',
    displayName: 'Replace multiple'
  },
  'word_create_edit_plan_tool': { 
    category: 'edit',
    displayName: 'Create edit plan',
    hidden: true
  },
  'word_mark_as_finished_tool': { 
    category: 'edit',
    displayName: 'Mark as finished',
    hidden: true
  },
  'wordaddheading': { 
    category: 'edit',
    displayName: 'Add heading'
  },
  'wordaddparagraphs': { 
    category: 'edit',
    displayName: 'Add paragraphs'
  },
  'wordaddtable': { 
    category: 'edit',
    displayName: 'Add table'
  },
  'wordreplaceinparagraph': { 
    category: 'edit',
    displayName: 'Replace in paragraph'
  },
  'wordformattable': { 
    category: 'edit',
    displayName: 'Format table'
  },
  'worddeleteparagraph': { 
    category: 'edit',
    displayName: 'Delete paragraph'
  },
  'worddeletetable': { 
    category: 'edit',
    displayName: 'Delete table'
  },
  'wordformatcelltext': { 
    category: 'edit',
    displayName: 'Format cell text'
  },
  'wordinsertnumberedlist': { 
    category: 'edit',
    displayName: 'Insert numbered list'
  },
  
  // Tools de búsqueda/listado
  'list_documents': { 
    category: 'list',
    displayName: 'List documents'
  },
  'get_document_content': { 
    category: 'search',
    displayName: 'Get document content'
  },
  'wordgetdocumenttext': {
    category: 'search',
    displayName: 'Get document text'
  },
  'templatecontent': {
    category: 'search',
    displayName: 'Template content',
    icon: Search
  },
  
  // Tools de web
  'websearch': {
    category: 'web',
    displayName: 'Web search'
  },
  'web_researcher': {
    category: 'web',
    displayName: 'Web researcher',
    hidden: true
  },
  'readwebpage': {
    category: 'web',
    displayName: 'Read webpage',
    hidden: true
  },
  'finishresearch': {
    category: 'web',
    displayName: 'Finish research',
    hidden: true
  },
};

// Iconos por defecto por categoría
const categoryIcons: Record<ToolCategory, LucideIcon> = {
  'edit': FileEdit,
  'search': Search,
  'web': Globe,
  'list': List,
  'default': CheckCircle2,
};

// Assets por defecto por categoría
const categoryAssets: Partial<Record<ToolCategory, string>> = {
  'edit': 'assets/icons/microsoft-word-icon.svg',
  'search': 'assets/icons/file-folder-icon.svg',
  'list': 'assets/icons/file-folder-icon.svg',
};

// Colores por defecto por categoría
const categoryColors: Record<ToolCategory, string> = {
  'edit': 'text-blue-500',
  'search': 'text-emerald-500',
  'web': 'text-blue-500',
  'list': 'text-emerald-500',
  'default': 'text-emerald-500',
};

// Función helper para normalizar nombres de tools
function normalizeToolName(toolName: string): string {
  return toolName.toLowerCase().replace(/[-_]/g, '_').trim();
}

// Función para obtener la configuración de una tool
export function getToolConfig(toolName: string): ToolConfig {
  const normalized = normalizeToolName(toolName);
  
  // Buscar match exacto
  if (toolConfigs[normalized]) {
    return toolConfigs[normalized];
  }
  
  // Buscar por patrones (fallback)
  if (normalized.includes('word') && (
    normalized.includes('search') || 
    normalized.includes('replace') || 
    normalized.includes('edit') || 
    normalized.includes('find')
  )) {
    return { category: 'edit' };
  }
  
  if (normalized.includes('list') && normalized.includes('document')) {
    return { category: 'list' };
  }
  
  if (normalized.includes('get') && normalized.includes('document') && normalized.includes('content')) {
    return { category: 'search' };
  }
  
  // Default
  return { category: 'default' };
}

// Función para obtener el nombre de display (alias o original)
export function getToolDisplayName(toolName: string): string {
  const config = getToolConfig(toolName);
  return config.displayName || toolName;
}

// Función para obtener el icono de una tool
export function getToolIcon(toolName: string): { Icon: LucideIcon; assetPath?: string; color: string } {
  const config = getToolConfig(toolName);
  const Icon = config.icon || categoryIcons[config.category];
  const assetPath = config.assetPath || categoryAssets[config.category];
  const color = config.iconColor || categoryColors[config.category];
  
  return { Icon, assetPath, color };
}

